import { useEffect, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { Keyboard, Mic, Square, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { speakableText, type ChatMessage } from './chatRows'
import type { CopilotChat } from './useCopilotChat'

// ── Voice mode (F-AI-51) ────────────────────────────────────────────────────
// A full-screen take on the chat: tap the orb, speak, and the reply is read
// back. The orb breathes while idle, swells with the mic while listening,
// turns while the copilot thinks and pulses while it speaks. Everything it
// does goes through useCopilotChat with autoVoice on; closing turns it off.

export type VoiceStage = 'idle' | 'listening' | 'thinking' | 'speaking'

export function voiceStage(chat: Pick<CopilotChat, 'voice' | 'sending' | 'speech'>): VoiceStage {
  if (chat.voice.isRecording) return 'listening'
  if (chat.voice.transcribing || chat.sending) return 'thinking'
  if (chat.speech.voiceState?.status === 'playing' || chat.speech.voiceState?.status === 'loading') return 'speaking'
  return 'idle'
}

const CAPTION: Record<VoiceStage, string> = {
  idle: 'Tap the orb and speak — Urdu or English',
  listening: 'Listening… tap again when you are done',
  thinking: 'Thinking…',
  speaking: 'Speaking — tap to stop',
}

function lastOf(messages: ChatMessage[], role: ChatMessage['role']): ChatMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === role && !messages[i].pending) return messages[i]
  return undefined
}

export function VoiceMode({ chat, onClose }: { chat: CopilotChat; onClose: () => void }) {
  const { voice, speech, messages, setAutoVoice } = chat
  const stage = voiceStage(chat)
  // The overlay appears under the finger that opened it. Ignore pointer input
  // for a moment so a slow tap (or a double-dispatched click) cannot land on
  // "Type instead" or the close button and shut it straight away.
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    const t = window.setTimeout(() => setArmed(true), 350)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    setAutoVoice(true)
    return () => setAutoVoice(false)
  }, [setAutoVoice])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const tap = () => {
    if (stage === 'idle') void voice.startRecording()
    else if (stage === 'listening') voice.stopRecording()
    else if (stage === 'speaking') speech.stopPlayback()
  }

  const close = () => {
    if (voice.isRecording) voice.stopRecording()
    speech.stopPlayback()
    onClose()
  }

  const lastUser = lastOf(messages, 'user')
  const lastReply = lastOf(messages, 'assistant')
  // The orb: a breathing base, mic swell on top, slow spin while thinking.
  const scale = useMemo(() => 1 + (stage === 'listening' ? voice.level * 0.7 : 0), [stage, voice.level])

  return (
    <motion.div
      role="dialog"
      aria-label="Bhookly AI voice mode"
      data-ai-voice=""
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      // Voice mode is dark in both themes on purpose: the gradient needs a
      // black stage, so the colour is set here, not from the palette.
      style={{ backgroundColor: '#0a0a0a' }}
      className={cn('fixed inset-0 z-[70] flex flex-col overflow-hidden text-white', !armed && 'pointer-events-none')}
    >
      {/* Living background */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(60% 50% at 20% 30%, rgba(251,146,60,0.45), transparent 60%), radial-gradient(50% 50% at 80% 70%, rgba(244,63,94,0.40), transparent 60%), radial-gradient(40% 40% at 60% 20%, rgba(250,204,21,0.25), transparent 60%)',
          backgroundSize: '160% 160%',
        }}
        animate={{ backgroundPosition: ['0% 0%', '100% 50%', '50% 100%', '0% 0%'] }}
        transition={{ duration: stage === 'idle' ? 24 : 10, repeat: Infinity, ease: 'linear' }}
      />

      <div className="relative flex items-center justify-between px-4 py-3">
        <span className="text-sm font-medium text-white/80">Bhookly AI · Voice</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={close}
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-white/80 hover:bg-white/10"
          >
            <Keyboard className="h-4 w-4" aria-hidden /> Type instead
          </button>
          <button
            type="button"
            onClick={close}
            aria-label="Close voice mode"
            className="rounded-md p-1.5 text-white/80 hover:bg-white/10"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>

      <div className="relative flex flex-1 flex-col items-center justify-center gap-8 px-6">
        <button
          type="button"
          onClick={tap}
          disabled={stage === 'thinking'}
          aria-label={CAPTION[stage]}
          className="relative flex h-56 w-56 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:cursor-default"
        >
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-300 via-orange-500 to-rose-500 blur-2xl"
            animate={
              stage === 'speaking'
                ? { scale: [1, 1.25, 1], opacity: [0.7, 1, 0.7] }
                : stage === 'thinking'
                  ? { scale: [1, 1.08, 1], rotate: [0, 180, 360], opacity: 0.9 }
                  : stage === 'listening'
                    ? { scale, opacity: 1 }
                    : { scale: [1, 1.06, 1], opacity: [0.6, 0.8, 0.6] }
            }
            transition={
              stage === 'listening'
                ? { type: 'spring', stiffness: 220, damping: 18 }
                : { duration: stage === 'speaking' ? 1.1 : stage === 'thinking' ? 2.4 : 4, repeat: Infinity, ease: 'easeInOut' }
            }
          />
          <motion.span
            aria-hidden
            className="absolute inset-6 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 shadow-2xl"
            animate={
              stage === 'speaking'
                ? { scale: [1, 1.08, 1] }
                : stage === 'thinking'
                  ? { rotate: [0, 360] }
                  : stage === 'listening'
                    ? { scale: 0.92 + voice.level * 0.5 }
                    : { scale: [1, 1.03, 1] }
            }
            transition={
              stage === 'listening'
                ? { type: 'spring', stiffness: 260, damping: 16 }
                : { duration: stage === 'speaking' ? 1.1 : stage === 'thinking' ? 3 : 4, repeat: Infinity, ease: 'linear' }
            }
          />
          <span className="relative text-white drop-shadow">
            {stage === 'listening' ? <Square className="h-9 w-9" aria-hidden /> : <Mic className="h-10 w-10" aria-hidden />}
          </span>
        </button>

        <p className="text-center text-sm text-white/80" role="status">
          {voice.micError ?? CAPTION[stage]}
        </p>

        <div className="w-full max-w-xl space-y-3 text-center">
          {lastUser && <p className="text-base text-white/70">“{lastUser.content}”</p>}
          {lastReply && (
            <p className={cn('whitespace-pre-wrap text-lg leading-relaxed', lastReply.system && 'text-white/70')}>
              {speakableText(lastReply.content)}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  )
}
